import { useMemo, useRef } from 'react';
import { scaleLinear } from 'd3-scale';
import { line as d3line, curveMonotoneX } from 'd3-shape';
import { motion } from 'framer-motion';
import { useTooltip } from '@nivo/tooltip';
import { BasicChartTooltip } from './tooltip';

interface SeriesLayerProps {
  key: string;
  series: { key: string; value: number | null }[];
  minSpan?: number;
  padRatio?: number;
  unit: string;
  label: string;
  color: string;
  animate?: boolean;
  duration?: number;
  yAxis?: boolean;
  reverseAxis?: boolean;
  showPoints?: boolean;
  getXLabelFormat?: (value: string) => string;
}

/**
 * A reusable layer for rendering a line series in a chart.
 * @param param0
 * @returns
 */

export function SeriesLayer({
  key,
  series,
  minSpan = 2,
  padRatio = 0.15,
  unit,
  label,
  color,
  animate = true,
  duration = 0.6,
  yAxis = false,
  reverseAxis = false,
  showPoints = true,
  getXLabelFormat,
}: SeriesLayerProps) {
  return function Layer(props: any) {
    const { xScale, innerHeight, innerWidth } = props;
    const { showTooltipFromEvent, hideTooltip } = useTooltip();
    const hasAnimated = useRef(false);

    const values = series.map((d) => d.value).filter((v): v is number => typeof v === 'number');
    if (values.length === 0) return null;

    const defaultDelay = 0.5;
    const min = Math.min(...values);
    const max = Math.max(...values);

    const span = max - min;
    const effectiveSpan = Math.max(span, minSpan);
    const center = (min + max) / 2;
    const pad = effectiveSpan * padRatio;

    const domainMin = center - effectiveSpan / 2 - pad;
    const domainMax = center + effectiveSpan / 2 + pad;

    const yValue = useMemo(
      () =>
        scaleLinear()
          .domain([domainMin, domainMax])
          .range(reverseAxis ? [0, innerHeight] : [innerHeight, 0])
          .nice(),
      [domainMin, domainMax, innerHeight, reverseAxis],
    );

    const pts = useMemo(
      () =>
        series
          .filter((d) => typeof d.value === 'number')
          .map((d) => ({
            key: d.key,
            x: xScale(d.key) + xScale.bandwidth() / 2,
            y: yValue(d.value as number),
            v: d.value as number,
          })),
      [series, xScale, yValue],
    );

    const path = useMemo(() => {
      const p = d3line<{ x: number; y: number }>()
        .x((d) => d.x)
        .y((d) => d.y)
        .curve(curveMonotoneX)(pts as any);
      return p ?? '';
    }, [pts]);

    const ticks = yValue.ticks(5);

    const shouldAnimate = animate && !hasAnimated.current;
    const pathInitial = shouldAnimate ? { pathLength: 0, opacity: 0 } : false;
    const pathAnimate = { pathLength: 1, opacity: 1 };

    const renderTooltip = (p: { key: string; v: number }) => {
      const timeLabel = getXLabelFormat ? getXLabelFormat(p.key) : p.key;
      return <BasicChartTooltip label={label} timeLabel={timeLabel} value={p.v.toFixed(1)} color={color} unit={unit} />;
    };

    const show = (event: React.MouseEvent, p: { key: string; v: number }) => {
      showTooltipFromEvent(renderTooltip(p), event, 'top');
    };

    return (
      <g>
        {path && (
          <motion.path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={2}
            pathLength={1}
            initial={pathInitial}
            animate={pathAnimate}
            transition={{ delay: defaultDelay, duration, ease: 'easeInOut' }}
            onAnimationComplete={() => {
              hasAnimated.current = true;
            }}
          />
        )}

        {showPoints &&
          pts.map((p, i) => (
            <motion.circle
              key={p.key}
              cx={p.x}
              cy={p.y}
              r={5}
              fill={color}
              initial={shouldAnimate ? { opacity: 0, scale: 0.6 } : false}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: defaultDelay + Math.min(duration * 0.6, 0.45) + i * 0.01, duration: 0.2 }}
              onMouseEnter={(e) => show(e, p)}
              onMouseMove={(e) => show(e, p)}
              onMouseLeave={hideTooltip}
            />
          ))}
        {yAxis && (
          <motion.g
            transform={`translate(${innerWidth},0)`}
            initial={shouldAnimate ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ delay: defaultDelay, duration: 0.2 }}
          >
            {ticks.map((t, i) => {
              const y = yValue(t);
              return (
                <g key={i} transform={`translate(0,${y})`}>
                  <text x={5} y={4} fontSize={11} fill="rgba(255, 255, 255, 0.4)">
                    {t}
                    {unit ? ` ${unit}` : ''}
                  </text>
                </g>
              );
            })}
          </motion.g>
        )}
      </g>
    );
  };
}
